import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ShareButton from "../components/ShareButton";
import SEO from "../components/SEO";
import { getBook, books, buyLink } from "../mock";
import { BookCover } from "./Resources";
import { ArrowLeft, ExternalLink } from "lucide-react";

const BookPage = () => {
  const { slug } = useParams();
  const book = getBook(slug);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [slug]);

  if (!book) {
    return (
      <div className="bg-[#eceae5] min-h-screen">
        <Navbar solid />
        <div className="pt-44 pb-40 text-center">
          <h1 className="font-display text-4xl text-neutral-900">Book not found</h1>
          <Link to="/resources" className="font-sans-ui text-neutral-600 underline mt-4 inline-block">
            Back to resources
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const more = books.filter((b) => b.slug !== book.slug).slice(0, 4);

  return (
    <div className="bg-[#eceae5] min-h-screen">
      <SEO title={book.title} description={book.blurb || book.subtitle} image={book.cover} type="book" />
      <Navbar solid />

      <div className="max-w-[1100px] mx-auto px-6 pt-32 md:pt-40 pb-16">
        <Link
          to="/resources"
          className="inline-flex items-center gap-2 font-sans-ui text-sm text-neutral-500 hover:text-neutral-900 transition-colors"
        >
          <ArrowLeft size={15} /> All books
        </Link>

        <div className="grid md:grid-cols-[280px_1fr] gap-10 md:gap-16 mt-8 items-start">
          <div className="max-w-[280px] mx-auto md:mx-0 w-full">
            <BookCover book={book} />
          </div>

          <div>
            <p className="font-sans-ui text-xs uppercase tracking-[0.2em] text-neutral-500">{book.tag}</p>
            <h1 className="font-display text-4xl md:text-5xl text-neutral-900 mt-3 leading-tight" style={{ fontWeight: 700 }}>
              {book.title}
            </h1>
            {book.subtitle && (
              <p className="font-display italic text-neutral-600 text-xl mt-2">{book.subtitle}</p>
            )}
            <p className="font-sans-ui text-sm text-neutral-500 mt-3">by {book.author}</p>

            <div className="mt-8">
              {book.why.map((p, i) => (
                <p key={`why-${i}`} className="font-display text-lg text-neutral-800 leading-relaxed mb-5">
                  {p}
                </p>
              ))}
            </div>

            {book.quotes && book.quotes.length > 0 && (
              <div className="mt-8">
                <h2 className="font-display text-2xl text-neutral-900" style={{ fontWeight: 700 }}>
                  Favorite Quotes
                </h2>
                <div className="mt-4 flex flex-col gap-4">
                  {book.quotes.map((q) => (
                    <blockquote
                      key={q}
                      className="border-l-2 border-neutral-400 pl-4 font-display italic text-neutral-700 leading-relaxed"
                    >
                      &ldquo;{q}&rdquo;
                    </blockquote>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a
                href={buyLink(book)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-neutral-900 hover:bg-neutral-700 text-white font-sans-ui text-sm px-7 py-3.5 rounded-full transition-colors"
              >
                <ExternalLink size={15} /> Find this book
              </a>
              <ShareButton title={`${book.title} by ${book.author}`} label="Share this pick" />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24 pt-8 border-t border-neutral-300">
        <p className="font-sans-ui uppercase tracking-[0.2em] text-sm text-neutral-500 mb-8">More reads</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {more.map((b) => (
            <Link key={b.slug} to={`/resources/${b.slug}`} className="group">
              <div className="transition-transform duration-500 group-hover:-translate-y-1.5">
                <BookCover book={b} />
              </div>
              <h3 className="font-display text-lg text-neutral-900 mt-3" style={{ fontWeight: 700 }}>{b.title}</h3>
              <p className="font-display italic text-neutral-600 text-sm">{b.author}</p>
            </Link>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BookPage;
